import { Stack, MenuList, MenuItem, ListItemIcon, ListItemText, Divider, Typography, IconButton } from "@mui/material";
import { AccountPopoverFooter, SignOutButton } from "@toolpad/core";
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { useLocation, useNavigate } from "react-router";
import { IUserSession } from "../interfaces/IUserSession";
import { useSession } from '../SessionContext';
import { paths } from "../routes/paths";
import { UserService } from "../services/userService";
import { useApiClient } from "../ApiClientProvider";

export default function CustomMenu() {
    const { session, setSession, signOut } = useSession<IUserSession>();
    const apiClient = useApiClient('managementClient');
    const userService = new UserService(apiClient);
    const navigate = useNavigate();
    const location = useLocation();
    const companies = session?.companies;
    const isInManageArea = location.pathname.startsWith('/manage');
    const partnerDashboardPath = isInManageArea ? paths.partnerDashboard : paths.public.partnerDashboard;
    const editProfilePath = isInManageArea ? paths.profile : paths.public.profile;

    const mapCompanyModeToSessionMode = (companyMode: string): 'light' | 'dark' => {
        if (companyMode === 'light' || companyMode === 'dark') {
            return companyMode;
        }
        return 'light';
    };

    const handleCompanySelect = async (id: string) => {
        const result = await userService.changeCompany(id);
        if (result && session) {
            // Update the session with the new token and company details
            const updatedSession: IUserSession = {
                ...session,
                user: {
                    id: result.id,
                    name: result.name,
                    email: result.email,
                },
                configuration: {
                    ...session.configuration,
                    selectedCompanyId: id,
                    mode: mapCompanyModeToSessionMode(result.companyMode),
                    primaryColour: result.companyPrimaryColour,
                    secondaryColour: result.companySecondaryColour,
                },
                token: result.token,
                roles: result.roles,
            };
            setSession(updatedSession);
            navigate(paths.dashboard, { replace: true });
        }
    };


    return (
        <Stack direction="column">
            <MenuList>
                <MenuItem key="profile" component="button" sx={{ justifyContent: 'flex-start', width: '100%', columnGap: 2, }} onClick={() => navigate(`${editProfilePath}?tab=editProfile`)}>
                    <ListItemIcon>
                        <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    < ListItemText
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            width: '100%',
                        }}
                        primary='Profile'
                    />
                </MenuItem>
                {session?.roles?.includes('Partner') && (
                    <MenuItem key="partner-dashboard" component="button" sx={{ justifyContent: 'flex-start', width: '100%', columnGap: 2, }} onClick={() => navigate(partnerDashboardPath)}>
                        <ListItemIcon>
                            <HandshakeIcon fontSize="small" />
                        </ListItemIcon>
                        < ListItemText
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                width: '100%',
                            }}
                            primary='Partner Dashboard'
                        />
                    </MenuItem>
                )}
                < Divider />
                <Typography variant="caption" fontStyle="italic" mx={2} mt={1} >
                    Companies
                </Typography>
                {
                    companies?.map((c: any) => (
                        <MenuItem
                            key={c.id}
                            component="button"
                            onClick={() => handleCompanySelect(c.id)}
                            selected={c.id.toLowerCase() === session?.configuration.selectedCompanyId?.toLowerCase()}
                            sx={{
                                justifyContent: 'flex-start',
                                width: '100%',
                                columnGap: 2,
                            }}
                        >
                            <ListItemIcon>
                                <BusinessIcon fontSize="small" />
                            </ListItemIcon>
                            < ListItemText
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    width: '100%',
                                }}
                                primary={c.name}
                            />
                            {session?.roles?.includes('Admin') && session?.configuration?.selectedCompanyId == c.id && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(paths.company.profile);
                                    }}
                                >
                                    <SettingsIcon fontSize="small" />
                                </IconButton>
                            )}
                        </MenuItem>
                    ))}
            </MenuList>
            < Divider />
            <AccountPopoverFooter>
                <SignOutButton onClick={() => signOut() } />
            </AccountPopoverFooter>
        </Stack>
    );
}
